import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import cs from "classnames";
import BadgeUI from "@/componentsUI/Badge";
import Portal from "@/componentsUI/Portal";
import { useTranslationPlus } from "@/Core/i18n/useTranslationPlus";
import { getMobileCenteredShellPortalRoot } from "@/layout/components/mobileCenteredShell/portal";
import { useGlobalState } from "@/store/commonStore";
import { getCurrentCurrency } from "@/utils/countryConfigUtils";
import useDialogManager from "@/utils/useDialogManager";
import { useRechargeLotteryInfo } from "@/store/rechargeLottery";
import { deriveRechargeSpinState } from "../activityState";
import {
  trackRechargeSpinEntryClick,
  trackRechargeSpinEntryView,
} from "../tracking";
import styles from "../index.module.less";
import entryIdle from "../assets/entry-idle.png";

const HIDDEN_ENTRY_STATES = new Set([
  "closed",
  "notStarted",
  "ended",
  "dailyLimit",
]);

const DEPOSIT_ENTRY_ANCHOR_SELECTOR =
  "[data-recharge-spin-entry-anchor='deposit-submit']";
const ENTRY_WIDTH = 78;
const ENTRY_HEIGHT = 86;
const ENTRY_BOUNDARY_MARGIN = 4;
const ENTRY_DRAG_THRESHOLD = 4;
const ENTRY_ANCHOR_RIGHT_OVERLAP = 51;
const ENTRY_ANCHOR_TOP_OFFSET = -31;

interface RechargeSpinEntryPosition {
  left: number;
  top: number;
}

interface RechargeSpinEntryDragState extends RechargeSpinEntryPosition {
  pointerId: number;
  pointerX: number;
  pointerY: number;
  hasMoved: boolean;
}

export const clampRechargeSpinEntryPosition = (
  position: RechargeSpinEntryPosition,
  portalRect: Pick<DOMRect, "width" | "height">
): RechargeSpinEntryPosition => {
  const maxLeft = Math.max(
    ENTRY_BOUNDARY_MARGIN,
    portalRect.width - ENTRY_WIDTH - ENTRY_BOUNDARY_MARGIN
  );
  const maxTop = Math.max(
    ENTRY_BOUNDARY_MARGIN,
    portalRect.height - ENTRY_HEIGHT - ENTRY_BOUNDARY_MARGIN
  );

  return {
    left: Math.min(Math.max(ENTRY_BOUNDARY_MARGIN, position.left), maxLeft),
    top: Math.min(Math.max(ENTRY_BOUNDARY_MARGIN, position.top), maxTop),
  };
};

const RechargeSpinEntry: React.FC = () => {
  const { t } = useTranslationPlus("rechargeSpinActivity");
  const { isLogin } = useGlobalState();
  const { openRechargeSpinActivity } = useDialogManager();
  const { data, isLoading } = useRechargeLotteryInfo();
  const currency = useMemo(() => getCurrentCurrency(), []);
  const [entryPosition, setEntryPosition] =
    useState<RechargeSpinEntryPosition>();
  const hasDraggedPositionRef = useRef(false);
  const dragStateRef = useRef<RechargeSpinEntryDragState>();
  const suppressNextClickRef = useRef(false);
  const suppressClickClearTimerRef = useRef<number>();
  const trackedEntryViewKeyRef = useRef<string>();
  const derived = deriveRechargeSpinState(data);
  const shouldShowEntry =
    isLogin &&
    !isLoading &&
    !!data &&
    data.status === 1 &&
    !HIDDEN_ENTRY_STATES.has(derived.availability);
  const entryState = derived.isReady
    ? "ready"
    : derived.progressPercent > 0
      ? "progress"
      : "idle";
  const trackingSource = "wallet_deposit";
  const viewTrackingKey = data
    ? [
        entryState,
        derived.availability,
        derived.progressPercent,
        data.availableDrawTimes,
        data.todayDrawTimes,
      ].join(":")
    : "";
  const updateAnchorPosition = useCallback(() => {
    if (typeof document === "undefined") {
      return;
    }

    const anchor = document.querySelector<HTMLElement>(
      DEPOSIT_ENTRY_ANCHOR_SELECTOR
    );
    const portalRoot = getMobileCenteredShellPortalRoot();

    if (!portalRoot) {
      if (!hasDraggedPositionRef.current) {
        setEntryPosition(undefined);
      }
      return;
    }

    const portalRect = portalRoot.getBoundingClientRect();

    if (!anchor) {
      if (hasDraggedPositionRef.current) {
        setEntryPosition((current) =>
          current
            ? clampRechargeSpinEntryPosition(current, portalRect)
            : undefined
        );
      } else {
        setEntryPosition(undefined);
      }
      return;
    }

    const anchorRect = anchor.getBoundingClientRect();
    const anchorPosition = clampRechargeSpinEntryPosition(
      {
        left:
          anchorRect.right - portalRect.left - ENTRY_ANCHOR_RIGHT_OVERLAP,
        top: anchorRect.top - portalRect.top + ENTRY_ANCHOR_TOP_OFFSET,
      },
      portalRect
    );

    setEntryPosition((current) => {
      if (!hasDraggedPositionRef.current) {
        return anchorPosition;
      }

      return current
        ? clampRechargeSpinEntryPosition(current, portalRect)
        : anchorPosition;
    });
  }, []);

  useEffect(() => {
    updateAnchorPosition();

    if (typeof window === "undefined") {
      return undefined;
    }

    const anchor = document.querySelector<HTMLElement>(
      DEPOSIT_ENTRY_ANCHOR_SELECTOR
    );
    const observer =
      anchor && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateAnchorPosition)
        : undefined;

    observer?.observe(anchor as HTMLElement);
    window.addEventListener("resize", updateAnchorPosition);
    window.addEventListener("scroll", updateAnchorPosition, true);

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updateAnchorPosition);
      window.removeEventListener("scroll", updateAnchorPosition, true);
      if (suppressClickClearTimerRef.current) {
        window.clearTimeout(suppressClickClearTimerRef.current);
      }
    };
  }, [updateAnchorPosition]);

  useEffect(() => {
    if (!shouldShowEntry || !data) {
      return;
    }

    if (trackedEntryViewKeyRef.current !== viewTrackingKey) {
      trackedEntryViewKeyRef.current = viewTrackingKey;
      trackRechargeSpinEntryView({
        source: trackingSource,
        entryState,
        info: data,
        derived,
      });
    }
  }, [data, derived, entryState, shouldShowEntry, viewTrackingKey]);

  if (!shouldShowEntry || !data) {
    return null;
  }

  const clearSuppressClickTimer = () => {
    if (!suppressClickClearTimerRef.current) {
      return;
    }

    window.clearTimeout(suppressClickClearTimerRef.current);
    suppressClickClearTimerRef.current = undefined;
  };

  const scheduleSuppressClickClear = () => {
    clearSuppressClickTimer();
    suppressClickClearTimerRef.current = window.setTimeout(() => {
      suppressNextClickRef.current = false;
      suppressClickClearTimerRef.current = undefined;
    }, 0);
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.pointerType === "mouse" && event.button !== 0) {
      return;
    }

    const portalRoot = getMobileCenteredShellPortalRoot();
    if (!portalRoot) {
      return;
    }

    const portalRect = portalRoot.getBoundingClientRect();
    const entryRect = event.currentTarget.getBoundingClientRect();
    const startPosition = clampRechargeSpinEntryPosition(
      entryPosition || {
        left: entryRect.left - portalRect.left,
        top: entryRect.top - portalRect.top,
      },
      portalRect
    );

    dragStateRef.current = {
      ...startPosition,
      pointerId: event.pointerId,
      pointerX: event.clientX,
      pointerY: event.clientY,
      hasMoved: false,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }

    const portalRoot = getMobileCenteredShellPortalRoot();
    if (!portalRoot) {
      return;
    }

    const deltaX = event.clientX - dragState.pointerX;
    const deltaY = event.clientY - dragState.pointerY;
    const hasMoved =
      dragState.hasMoved ||
      Math.abs(deltaX) > ENTRY_DRAG_THRESHOLD ||
      Math.abs(deltaY) > ENTRY_DRAG_THRESHOLD;

    if (!hasMoved) {
      return;
    }

    const portalRect = portalRoot.getBoundingClientRect();
    hasDraggedPositionRef.current = true;
    dragStateRef.current = {
      ...dragState,
      hasMoved,
    };
    setEntryPosition(
      clampRechargeSpinEntryPosition(
        {
          left: dragState.left + deltaX,
          top: dragState.top + deltaY,
        },
        portalRect
      )
    );
  };

  const handlePointerEnd = (event: React.PointerEvent<HTMLButtonElement>) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }

    if (dragState.hasMoved) {
      suppressNextClickRef.current = true;
      scheduleSuppressClickClear();
    }
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    dragStateRef.current = undefined;
  };

  const entryLabel = derived.isReady
    ? t("EntryReady", { count: data.availableDrawTimes })
    : t("EntryLocked", { currency });
  const trackingContext = {
    source: trackingSource,
    entryState,
    info: data,
    derived,
  } as const;
  const progressStyle = {
    "--entry-progress-deg": `${derived.progressPercent * 3.6}deg`,
    "--entry-progress-percent": derived.progressPercent,
    ...(entryPosition
      ? {
          left: entryPosition.left,
          top: entryPosition.top,
          right: "auto",
          bottom: "auto",
        }
      : undefined),
  } as React.CSSProperties;

  const entryButton = (
    <button
      type="button"
      className={cs(styles.entry, styles[`entry-${entryState}`])}
      aria-label={`${t("EntryTitle")} ${entryLabel}`}
      style={progressStyle}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
      onClick={(event) => {
        if (suppressNextClickRef.current) {
          event.preventDefault();
          event.stopPropagation();
          suppressNextClickRef.current = false;
          clearSuppressClickTimer();
          return;
        }

        trackRechargeSpinEntryClick(trackingContext);
        openRechargeSpinActivity({ source: trackingSource });
      }}
    >
      <BadgeUI
        className={styles.entryBadge}
        count={derived.isReady ? data.availableDrawTimes : 0}
      >
        <span className={styles.entryIcon}>
          {entryState === "progress" ? (
            <span className={styles.entryProgressRing} aria-hidden="true">
              <svg viewBox="0 0 78 78" focusable="false">
                <circle
                  className={styles.entryProgressTrail}
                  cx="39"
                  cy="39"
                  r="34"
                  pathLength="100"
                />
                <circle
                  className={styles.entryProgressFill}
                  cx="39"
                  cy="39"
                  r="34"
                  pathLength="100"
                />
              </svg>
            </span>
          ) : null}
          <span
            className={styles.entryIconImage}
            style={
              entryState === "ready"
                ? undefined
                : { backgroundImage: `url(${entryIdle})` }
            }
            aria-hidden="true"
          />
        </span>
      </BadgeUI>
      {entryState === "progress" ? (
        <span className={styles.entryPercent}>{derived.progressPercent}%</span>
      ) : null}
    </button>
  );

  return (
    <Portal visible getContainer={getMobileCenteredShellPortalRoot}>
      {entryButton}
    </Portal>
  );
};

export default RechargeSpinEntry;
